(() => {
  if (customElements.get('baxter-suite-package')) return;

  class BaxterSuitePackage extends HTMLElement {
    connectedCallback() {
      if (this.initialized) {
        if (this.handlePageShow) window.addEventListener('pageshow', this.handlePageShow);
        return;
      }
      this.initialized = true;
      this.pending = false;
      this.locked = false;
      this.confirmed = false;
      this.attempt = 0;
      this.button = this.querySelector('[data-add-package]');
      this.finishPicker = this.querySelector('[data-package-finish]');
      this.total = this.querySelector('[data-package-total]');
      this.message = this.querySelector('[data-package-message]');
      this.cartReview = this.querySelector('[data-cart-review]');

      try {
        this.labels = JSON.parse(this.querySelector('[data-package-labels]').textContent);
        const labelKeys = ['chooseOptions', 'unavailable', 'adding', 'add', 'unknownResult', 'confirmed', 'configurationIssue', 'finishPlaceholder', 'noCommonFinish', 'propertyLabel'];
        if (labelKeys.some((key) => typeof this.labels[key] !== 'string')) throw new Error();
        this.products = JSON.parse(this.querySelector('[data-package-data]').textContent);
        this.expectedCount = Number(this.getAttribute('data-expected-count'));
        this.suiteName = this.getAttribute('data-suite-name');
        this.cartUrl = this.getAttribute('data-cart-url');
        this.addUrl = this.getAttribute('data-add-url');
        const currency = this.getAttribute('data-currency');
        const locale = this.getAttribute('data-locale') || 'en';
        if (!/^[A-Z]{3}$/.test(currency)) throw new Error();
        this.formatter = new Intl.NumberFormat(locale, { style: 'currency', currency });
        if (!this.suiteName || !this.isLocalPath(this.cartUrl) || !this.isLocalPath(this.addUrl) || !this.addUrl.endsWith('/cart/add.js')) throw new Error();
        if (!this.button || !this.finishPicker || !this.total || !this.message || !this.cartReview) throw new Error();
        if (!Number.isInteger(this.expectedCount) || this.expectedCount < 1 || !Array.isArray(this.products) || this.products.length !== this.expectedCount) throw new Error();

        const productIds = new Set();
        const variantIds = new Set();
        for (const product of this.products) {
          product.id = this.normalizeId(product.id);
          if (!product.id || productIds.has(product.id) || typeof product.requiresSellingPlan !== 'boolean' || !Array.isArray(product.variants) || product.variants.length === 0) throw new Error();
          if (product.variantCount !== undefined && (!Number.isInteger(product.variantCount) || product.variantCount !== product.variants.length)) throw new Error();
          productIds.add(product.id);
          for (const variant of product.variants) {
            variant.id = this.normalizeId(variant.id);
            if (!variant.id || variantIds.has(variant.id) || typeof variant.title !== 'string' || !variant.title || typeof variant.finish !== 'string' || !variant.finish || typeof variant.formattedPrice !== 'string' || !Number.isSafeInteger(variant.price) || variant.price < 0 || typeof variant.available !== 'boolean') throw new Error();
            variantIds.add(variant.id);
          }
        }

        const rows = Array.from(this.querySelectorAll('[data-package-product]'));
        if (rows.length !== this.expectedCount) throw new Error();
        const rowIds = new Set();
        this.rows = rows.map((element) => {
          const id = this.normalizeId(element.getAttribute('data-package-product'));
          const product = this.products.find((item) => item.id === id);
          const picker = element.querySelector('[data-variant-picker]');
          const price = element.querySelector('[data-item-price]');
          if (!product || rowIds.has(id) || !picker || !price) throw new Error();
          rowIds.add(id);
          return { product, picker, price };
        });

        const finishes = this.products.map((product) => new Set(product.variants.filter((variant) => this.isEligible(product, variant)).map((variant) => variant.finish)));
        this.commonFinishes = Array.from(finishes[0]).filter((finish) => finishes.every((set) => set.has(finish)));
        this.finishPicker.replaceChildren(new Option(this.labels.finishPlaceholder, ''), ...this.commonFinishes.map((finish) => new Option(finish, finish)));
        this.finishPicker.value = '';
        this.cartReview.hidden = true;
        this.addEventListener('change', (event) => {
          if (this.pending || this.confirmed) return;
          if (event.target === this.finishPicker) this.chooseFinish();
          if (event.target === this.finishPicker || this.rows.some((row) => row.picker === event.target)) this.update();
        });
        this.button.addEventListener('click', () => this.submit());
        this.handlePageShow = (event) => {
          if (!event.persisted) return;
          if (this.pending) {
            this.attempt += 1;
            this.showUnknown();
          } else if (this.confirmed) {
            this.confirmed = false;
            this.locked = false;
            this.message.textContent = '';
            this.cartReview.hidden = true;
            this.update();
          }
        };
        window.addEventListener('pageshow', this.handlePageShow);
        this.chooseFinish();
        this.update();
      } catch {
        this.configurationFailed = true;
        if (this.button) this.button.disabled = true;
        if (this.finishPicker) this.finishPicker.disabled = true;
        for (const picker of this.querySelectorAll('[data-variant-picker]')) picker.disabled = true;
        if (this.message) this.message.textContent = this.labels?.configurationIssue || '';
        if (this.cartReview) this.cartReview.hidden = true;
      }
    }

    disconnectedCallback() {
      if (this.handlePageShow) window.removeEventListener('pageshow', this.handlePageShow);
    }

    normalizeId(value) {
      if (typeof value === 'number' && !Number.isSafeInteger(value)) return null;
      const id = String(value);
      return /^[1-9]\d*$/.test(id) ? id : null;
    }

    isLocalPath(value) {
      return typeof value === 'string' && value.startsWith('/') && !value.startsWith('//') && !value.includes('\\');
    }

    isEligible(product, variant) {
      return !product.requiresSellingPlan && variant.available && Number.isInteger(variant.quantityMin) && variant.quantityMin >= 1 && variant.quantityMin <= 1 && variant.quantityIncrement === 1 && (variant.quantityMax === null || (Number.isInteger(variant.quantityMax) && variant.quantityMax >= 1));
    }

    chooseFinish() {
      const finish = this.finishPicker.value;
      const validFinish = this.commonFinishes.includes(finish);
      for (const row of this.rows) {
        const variants = validFinish ? row.product.variants.filter((variant) => variant.finish === finish) : [];
        const options = variants.map((variant) => {
          const option = new Option(variant.title, variant.id);
          option.disabled = !this.isEligible(row.product, variant);
          return option;
        });
        row.picker.replaceChildren(new Option(this.labels.chooseOptions, ''), ...options);
        const eligible = variants.filter((variant) => this.isEligible(row.product, variant));
        row.picker.value = eligible.length === 1 ? eligible[0].id : '';
      }
    }

    getSelections() {
      const finish = this.finishPicker.value;
      if (!this.commonFinishes.includes(finish)) return null;
      const selections = this.rows.map((row) => row.product.variants.find((variant) => variant.id === row.picker.value && variant.finish === finish && this.isEligible(row.product, variant)));
      return selections.every(Boolean) ? selections : null;
    }

    update() {
      if (this.configurationFailed) return;
      const finish = this.finishPicker.value;
      const validFinish = this.commonFinishes.includes(finish);
      const selections = this.getSelections();
      let subtotal = 0;
      for (const row of this.rows) {
        const variant = row.product.variants.find((item) => item.id === row.picker.value && item.finish === finish && this.isEligible(row.product, item));
        row.price.textContent = variant ? variant.formattedPrice : this.labels.chooseOptions;
        if (variant) subtotal += variant.price;
        row.picker.disabled = !validFinish || this.pending || this.confirmed;
      }
      const validSubtotal = selections && Number.isSafeInteger(subtotal);
      this.total.textContent = validSubtotal ? this.formatter.format(subtotal / 100) : this.labels.chooseOptions;
      this.finishPicker.disabled = this.commonFinishes.length === 0 || this.pending || this.confirmed;
      this.button.disabled = !validSubtotal || this.pending || this.locked || this.confirmed;
      this.button.textContent = this.pending ? this.labels.adding : this.labels.add;
      this.setAttribute('aria-busy', this.pending ? 'true' : 'false');
      if (!this.pending && !this.locked && !this.confirmed) {
        this.message.textContent = this.commonFinishes.length === 0 ? this.labels.noCommonFinish : selections && !validSubtotal ? this.labels.configurationIssue : '';
      }
    }

    showUnknown() {
      this.pending = false;
      this.locked = true;
      this.confirmed = false;
      this.message.textContent = this.labels.unknownResult;
      this.cartReview.hidden = false;
      this.update();
    }

    confirmsItems(payload, selections) {
      if (!Array.isArray(payload?.items)) return false;
      return selections.every((variant) => payload.items.some((item) => this.normalizeId(item.variant_id ?? item.id) === variant.id && Number.isInteger(item.quantity) && item.quantity >= 1));
    }

    async submit() {
      if (this.configurationFailed || this.pending || this.locked || this.confirmed) return;
      const selections = this.getSelections();
      if (!selections || this.button.disabled) return;
      const attempt = ++this.attempt;
      this.pending = true;
      this.message.textContent = this.labels.adding;
      this.cartReview.hidden = true;
      this.update();

      try {
        const response = await fetch(this.addUrl, {
          method: 'POST',
          credentials: 'same-origin',
          headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
          body: JSON.stringify({ items: selections.map((variant) => ({ id: variant.id, quantity: 1, properties: { [this.labels.propertyLabel]: this.suiteName } })) }),
        });
        const payload = await response.json();
        if (attempt !== this.attempt) return;
        if (!response.ok || !Number.isInteger(response.status) || response.status < 200 || response.status >= 300 || !this.confirmsItems(payload, selections)) {
          this.showUnknown();
          return;
        }
        this.pending = false;
        this.confirmed = true;
        this.message.textContent = this.labels.confirmed;
        this.update();
        window.location.assign(this.cartUrl);
      } catch {
        if (attempt === this.attempt) this.showUnknown();
      }
    }
  }

  customElements.define('baxter-suite-package', BaxterSuitePackage);
})();
